<template>
    <div class="selection:bg-brand-500 selection:text-white pb-20 overflow-x-hidden">
        <!-- Hero Section -->
        <header class="relative pt-20 pb-24 sm:pt-32 sm:pb-32 bg-card/30 border-b border-app">
            <div
                class="absolute inset-0 -z-10 bg-[radial-gradient(45%_45%_at_50%_0%,rgba(var(--core-brand-500),0.05)_0%,transparent_100%)]">
            </div>
            <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
                <h1
                    class="text-base font-semibold leading-7 text-brand-500 uppercase tracking-widest mb-6 px-4 py-1.5 rounded-full border border-brand-500/10 bg-brand-500/5 inline-block">
                    Get in Touch</h1>
                <p
                    class="mx-auto max-w-4xl text-5xl font-extrabold tracking-tight text-header sm:text-7xl leading-[1.1]">
                    Let's build something <span
                        class="bg-gradient-to-r to-brand-500 via-primary-500 from-secondary-500 bg-clip-text text-transparent font-extrabold">Extraordinary together.</span>
                    
                </p>
                <p class="mx-auto mt-8 max-w-2xl text-xl text-body/80 leading-relaxed">
                    Have a question, a project idea, or just want to say hello? Our team is always ready to connect and
                    explore new possibilities.
                </p>
            </div>
        </header>

        <!-- Contact Content -->
        <section class="py-24">
            <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
                    <!-- Contact Form -->
                    <div class="bg-card p-10 rounded-[40px] border border-app shadow-2xl relative overflow-hidden">
                        <div class="absolute -top-24 -left-24 h-64 w-64 bg-brand-500/5 blur-[100px]"></div>
                        <form @submit.prevent="submitForm" class="relative z-10 space-y-6">
                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div class="space-y-2">
                                    <label class="text-sm font-bold text-header ml-1">Full Name</label>
                                    <input v-model="form.name" type="text" placeholder="John Doe"
                                        class="w-full rounded-2xl border border-app bg-app px-6 py-4 text-header focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/10 transition-all"
                                        required />
                                </div>
                                <div class="space-y-2">
                                    <label class="text-sm font-bold text-header ml-1">Email Address</label>
                                    <input v-model="form.email" type="email" placeholder="john@example.com"
                                        class="w-full rounded-2xl border border-app bg-app px-6 py-4 text-header focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/10 transition-all"
                                        required />
                                </div>
                            </div>
                            <div class="space-y-2">
                                <label class="text-sm font-bold text-header ml-1">Subject</label>
                                <select v-model="form.subject"
                                    class="w-full rounded-2xl border border-app bg-app px-6 py-4 text-header focus:border-brand-500 focus:outline-none transition-all appearance-none cursor-pointer">
                                    <option value="general">General Inquiry</option>
                                    <option value="support">Technical Support</option>
                                    <option value="sales">Sales & Partnerships</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                            <div class="space-y-2">
                                <label class="text-sm font-bold text-header ml-1">Message</label>
                                <textarea v-model="form.message" rows="5" placeholder="Tell us how we can help..."
                                    class="w-full rounded-2xl border border-app bg-app px-6 py-4 text-header focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/10 transition-all resize-none"
                                    required></textarea>
                            </div>
                            <button type="submit"
                                class="w-full group rounded-2xl bg-brand-500 py-5 text-lg font-bold text-white transition-all hover:bg-brand-600 hover:shadow-xl hover:shadow-brand-500/25 active:scale-95 flex items-center justify-center gap-2">
                                Send Message
                                <Send
                                    class="h-5 w-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                            </button>
                        </form>
                    </div>

                    <!-- Contact Info -->
                    <div class="flex flex-col justify-center space-y-12">
                        <div class="space-y-6">
                            <h2 class="text-3xl font-bold text-header">Contact Information</h2>
                            <p class="text-body/80 leading-relaxed max-w-md">Our headquarters are located in the heart of
                                the innovation district. Drop by or reach out through any of these channels.</p>
                        </div>

                        <div class="space-y-8">
                            <div v-for="info in contactInfo" :key="info.label" class="flex gap-6 group">
                                <div
                                    class="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-brand-500/10 text-brand-500 transition-all group-hover:bg-brand-500 group-hover:text-white group-hover:scale-110 shadow-sm">
                                    <component :is="info.icon" class="h-6 w-6" />
                                </div>
                                <div>
                                    <h4 class="text-sm font-bold text-body/80 uppercase tracking-widest mb-1">{{
                                        info.label }}</h4>
                                    <p
                                        class="text-xl font-bold text-header group-hover:text-brand-500 transition-colors">
                                        {{ webConfig[info.value] }}</p>
                                </div>
                            </div>
                        </div>

                        <div class="pt-8 border-t border-app">
                            <h4 class="text-sm font-bold text-body/80 uppercase tracking-widest mb-6">Social Connect</h4>
                            <div class="flex gap-4">
                                <a v-for="social in socials" :key="social.name" href="#"
                                    class="h-12 w-12 rounded-xl bg-app border border-app flex items-center justify-center text-header hover:text-white hover:bg-brand-500 hover:border-brand-500 transition-all">
                                    <component :is="social.icon" class="h-5 w-5" />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Map/Location Section -->
        <section class="py-24 bg-body border-t border-app">
            <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div class="relative h-[500px] w-full rounded-[40px] overflow-hidden border border-app shadow-2xl">
                    <img src="https://images.unsplash.com/photo-1548345666-a52145e12e84?auto=format&fit=crop&q=80&w=2000"
                        class="w-full h-full object-cover grayscale opacity-50 contrast-125" />
                    <div class="absolute inset-0 bg-gradient-to-t from-app via-transparent to-transparent"></div>
                    <!-- Mock Marker -->
                    <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                        <div class="relative flex h-10 w-10">
                            <span
                                class="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-400 opacity-75"></span>
                            <span
                                class="relative flex rounded-full h-10 w-10 bg-brand-500 border-4 border-white shadow-xl items-center justify-center">
                                <MapPin class="h-4 w-4 text-white" />
                            </span>
                        </div>
                    </div>
                    <div
                        class="absolute bottom-10 left-10 right-10 md:left-20 md:right-auto md:max-w-md bg-card/80 backdrop-blur-xl p-8 rounded-3xl border border-white/10 shadow-2xl">
                        <h4 class="text-xl font-bold text-header mb-2">{{ webConfig.tenantName }}</h4>
                        <p class="text-body/80 text-sm leading-relaxed mb-6">{{ webConfig.contactAddress }}</p>
                        <button class="flex items-center gap-2 text-brand-500 font-bold hover:gap-3 transition-all">
                            Get Directions
                            <ArrowRight class="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue'
import {
    Send,
    Mail,
    Phone,
    MapPin,
    Clock,
    Twitter,
    Linkedin,
    Github,
    Instagram,
    ArrowRight
} from 'lucide-vue-next'
import { useGeneralStore } from '@core/global';
import { WebConfig } from '@core/stores/types';

definePageMeta({ name: 'Contact' });

const generalStore = useGeneralStore();
const webConfig = computed<WebConfig>(() => generalStore.webConfig);

const form = reactive({
    name: '',
    email: '',
    subject: 'general',
    message: ''
})


const contactInfo = [
    { label: 'Email Address', value: 'contactEmail', icon: Mail },
    { label: 'Phone Number', value: 'contactPhone', icon: Phone },
    // { label: 'Business Hours', value: 'contactHours', icon: Clock }
    { label: 'Business Address', value: 'contactAddress', icon: Clock }
]

const socials = [
    { name: 'Twitter', icon: Twitter },
    { name: 'LinkedIn', icon: Linkedin },
    { name: 'GitHub', icon: Github },
    { name: 'Instagram', icon: Instagram }
]

const submitForm = () => {
    alert('Thank you for your message! Our team will get back to you shortly (Mock Demo).')
    form.name = ''
    form.email = ''
    form.message = ''
}
</script>

<style scoped>
.bg-card {
    background-color: rgb(var(--core-card-bg));
}

.bg-app {
    background-color: rgb(var(--core-app-bg));
}

.bg-body {
    background-color: rgb(var(--core-body-bg));
}

.border-app {
    border-color: rgb(var(--core-app-border));
}

.text-header {
    color: rgb(var(--core-header-text));
}

.text-muted {
    color: rgb(var(--core-muted-text));
}
</style>
